import { useEventListener } from "@vueuse/core";
import { useHost } from "vue";
import { requestConfirmation } from "#ce/components/SpConfirm/request-confirmation";
import { t, whenComponentsI18nReady } from "#ce/shared/components-i18n";
import { toBoolean } from "#ce/utils/props";
import { createActionFormApi } from "#ce/components/SpActionForm/api/action-form";

const ActionFormStatus = Object.freeze({
  IDLE: "idle",
  CONFIRMING: "confirming",
  PENDING: "pending",
});

const ActionFormEvent = Object.freeze({
  SEND: "action-send",
  SUCCESS: "action-success",
  ERROR: "action-error",
  COMPLETE: "action-complete",
});

const SUBMIT_ITEM = 'sp-button[type="submit"]';
const PENDING_ATTRIBUTES = ["loading", "disabled"];
const SAME_WINDOW_TARGETS = ["", "_self", "_top", "_parent"];

// An attribute that is absent or "false" leaves the `sp-button` state off.
function isOff(item, name) {
  const value = item.getAttribute(name);

  return value === null || value === "false";
}

// Turns `item` loading and disabled and marks `form` busy. Returns a function that puts back the
// form's own `aria-busy` and the item attributes this call turned on.
function markPending(form, item) {
  const previous = PENDING_ATTRIBUTES.filter((name) => item && isOff(item, name)).map((name) => [
    name,
    item.getAttribute(name),
  ]);
  const previousBusy = form.getAttribute("aria-busy");

  previous.forEach(([name]) => item.setAttribute(name, ""));
  form.setAttribute("aria-busy", "true");

  return function restore() {
    previous.forEach(([name, value]) => (value === null ? item.removeAttribute(name) : item.setAttribute(name, value)));

    if (previousBusy === null) {
      form.removeAttribute("aria-busy");
    } else {
      form.setAttribute("aria-busy", previousBusy);
    }
  };
}

function leavesPage(form, submitter) {
  const target = submitter?.getAttribute("formtarget") ?? form.target;

  return SAME_WINDOW_TARGETS.includes(target.trim().toLowerCase());
}

/**
 * Sends an `sp-action-form`'s slotted form: asks `props.confirm` first when it is not blank, marks
 * the pressed submit `sp-button` pending (the form's first one when none was pressed), and reports
 * the request through bubbling `action-*` events whose `detail` is an object. With `props.navigate`
 * the form is submitted again with its original submitter for the browser to carry out, and only
 * `action-send` is dispatched once that submission goes out. A submission while one is still
 * confirming or pending is dropped.
 *
 * @param {Object} props - The element's props.
 * @returns {{ handleClick: (event: MouseEvent) => void, handleSubmit: (event: SubmitEvent) => void }}
 *   `handleClick` remembers the submit item a click lands on, for that click only, because a
 *   submit `sp-button` reaches the form with no `submitter`. `handleSubmit` cancels the native submission and sends the form, unless another listener has
 *   already cancelled it.
 */
export function useActionForm(props) {
  const api = createActionFormApi();
  const host = useHost();
  let status = ActionFormStatus.IDLE;
  let pressedItem = null;
  let replay = null;
  let restorePending = null;

  function settlePending() {
    restorePending?.();
    restorePending = null;
    status = ActionFormStatus.IDLE;
  }

  function settleOnReturn(event) {
    if (event.persisted && restorePending) {
      settlePending();
    }
  }

  useEventListener(window, "pageshow", settleOnReturn);

  function dispatch(type, detail) {
    host.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: false }));
  }

  function hasConfirmation() {
    return typeof props.confirm === "string" && props.confirm.trim() !== "";
  }

  // Labels are read once per question, so the translations are awaited first.
  async function askConfirmation() {
    await whenComponentsI18nReady;

    return requestConfirmation({
      title: t("action_form_confirm_title"),
      content: props.confirm,
      confirmLabel: t("action_form_confirm"),
      cancelLabel: t("action_form_cancel"),
    });
  }

  async function send(form, item, submitter) {
    if (status === ActionFormStatus.CONFIRMING && !(await askConfirmation())) {
      status = ActionFormStatus.IDLE;
      return;
    }

    status = ActionFormStatus.PENDING;

    if (toBoolean(props.navigate)) {
      setTimeout(function replayLater() {
        submitNatively(form, item, submitter);
      });
      return;
    }

    const restore = markPending(form, item);
    let outcome;

    dispatch(ActionFormEvent.SEND, { form });

    try {
      outcome = [ActionFormEvent.SUCCESS, { form, data: await api.submit(form) }];
    } catch (error) {
      outcome = [ActionFormEvent.ERROR, { form, error }];
    }

    restore();
    status = ActionFormStatus.IDLE;
    dispatch(...outcome);
    dispatch(ActionFormEvent.COMPLETE, { form });
  }

  // Submits again with the original submitter, so the browser validates the form and applies the
  // submitter's value and overrides. Runs in a task after the original submit event, because a
  // `requestSubmit` while that event is firing does nothing. Returns whether that submission went
  // out uncancelled.
  function replaySubmission(form, submitter) {
    const submission = { event: null };

    replay = submission;

    try {
      form.requestSubmit(submitter ?? undefined);
    } catch {
      return false;
    } finally {
      replay = null;
    }

    return Boolean(submission.event) && !submission.event.defaultPrevented;
  }

  // The item is marked once the submission went out, so a native submitter is still enabled when
  // the browser reads it. It stays pending while the page leaves, and is restored when the page
  // comes back from the back/forward cache or when the submission opens another window.
  function submitNatively(form, item, submitter) {
    if (!replaySubmission(form, submitter)) {
      status = ActionFormStatus.IDLE;
      return;
    }

    restorePending = markPending(form, item);
    dispatch(ActionFormEvent.SEND, { form });

    if (!leavesPage(form, submitter)) {
      settlePending();
    }
  }

  function forgetPressedItem() {
    pressedItem = null;
  }

  // A submit item submits inside its own click, so the remembered item is dropped in a task
  // after it. Microtasks run between a click's listeners, before the item's own one.
  function handleClick(event) {
    const item = event.target.closest?.(SUBMIT_ITEM);

    if (item) {
      pressedItem = item;
      setTimeout(forgetPressedItem);
    }
  }

  function handleSubmit(event) {
    if (replay) {
      replay.event = event;
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    const form = event.target;
    const submitter = event.submitter;
    const item = submitter ?? pressedItem ?? form.querySelector(SUBMIT_ITEM);

    event.preventDefault();
    pressedItem = null;

    if (status !== ActionFormStatus.IDLE) {
      return;
    }

    status = hasConfirmation() ? ActionFormStatus.CONFIRMING : ActionFormStatus.PENDING;
    void send(form, item, submitter);
  }

  return { handleClick, handleSubmit };
}
